import { useCallback, useState } from "react";

export const MAX_QUICK_FILTERS = 10;

const read = (storageKey: string | null): string[] | null => {
  if (!storageKey) return null;
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey) || "null");
    if (Array.isArray(raw) && raw.every((x) => typeof x === "string")) return raw.slice(0, MAX_QUICK_FILTERS);
  } catch {
    // Storage unavailable or corrupt: use the page's own list
  }
  return null;
};

/**
 * The quick filters a person keeps on a list page (up to 10), remembered per browser. An entry is the name of one of the
 * page's own filters ("owner") or "f:<field>" for any field of the list. Until someone edits it, it is the page's default.
 */
export function useQuickFilters(key: string | undefined, defaults: string[]) {
  const storageKey = key ? `crm.quickfilters.${key}` : null;
  const [saved, setSaved] = useState<string[] | null>(() => read(storageKey));

  const save = useCallback(
    (next: string[] | null) => {
      const list = next ? Array.from(new Set(next)).slice(0, MAX_QUICK_FILTERS) : null;
      setSaved(list);
      if (!storageKey) return;
      try {
        if (list) localStorage.setItem(storageKey, JSON.stringify(list));
        else localStorage.removeItem(storageKey);
      } catch {
        // Not remembered
      }
    },
    [storageKey],
  );

  return { list: saved ?? defaults, isCustom: saved !== null, save, reset: () => save(null), editable: !!storageKey, max: MAX_QUICK_FILTERS };
}
